import React, { useState, ChangeEvent, KeyboardEvent, useRef } from 'react';
import { TextField, Chip, Box, InputAdornment } from '@mui/material';
import { Label, TurnedInNot } from '@mui/icons-material';

interface SingleValueInputProps {
  value: string;
  setValue: (v: string) => void;
  disabled?: boolean;
}

const SingleTextFieldWithChip: React.FC<SingleValueInputProps> = ({
  value,
  setValue,
  disabled,
}) => {
  const [inputValue, setInputValue] = useState<string>(''); // For adding or editing
  const [isEditing, setIsEditing] = useState<boolean>(false); // Whether in edit mode
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmedValue = inputValue.trim();
      setValue(trimmedValue);
      setInputValue('');
      setIsEditing(false);
    }
  };

  const handleBlur = (): void => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue) {
      setValue(trimmedValue);
    }
    setInputValue('');
    setIsEditing(false);
  };

  const handleEdit = (): void => {
    setInputValue(value);
    setIsEditing(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  return value && !isEditing ? (
    <Chip
      label={value}
      onClick={handleEdit}
      icon={<TurnedInNot />}
      disabled={disabled}
      sx={{ backgroundColor: '#e0e0e0', margin: 0.5 }}
    />
  ) : (
    <TextField
      autoComplete="off"
      value={inputValue}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      inputRef={inputRef}
      label={value ? 'Edit Value' : 'Add Value'}
      variant="outlined"
      disabled={disabled}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {inputValue && (
              <Chip
                label="Press Enter to save"
                size="small"
                color="secondary"
              />
            )}
          </InputAdornment>
        ),
      }}
    />
  );
};

export default SingleTextFieldWithChip;
